import { inject, Injectable } from '@angular/core';
import { HttpInterceptor, HttpEvent, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Observable, from, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { getKeycloakContext } from '../auth/auth.utils';
import { AuthService } from '../auth.service';
import { DynamicKeycloakService } from '../dynamic-keycloak.service';

const jwtTokenConfig = environment.jwtTokenConfig;
const BASE_API_URL = environment.apiURL;
const snagReportSettings = environment.snagReportSettings;

class AuthRefreshFailedError extends Error {
	constructor () {
		super( 'auth:refresh-failed' );
		this.name = 'AuthRefreshFailedError';
	}
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	private dynamicKeycloakService = inject( DynamicKeycloakService );
	private authService = inject( AuthService );

	private static readonly SKIP_AUTH_HEADER_URLS = ['/v3/admin/auth/register-contact', '/clientByUrl', '/socientaVersion'];

	intercept ( req: HttpRequest<any>, next: HttpHandler ): Observable<HttpEvent<any>> {
		// Skip for specific URLs like snag report
		if ( req.url.includes( snagReportSettings.api.url ) ) return next.handle( req );
		if ( !req.url.includes( BASE_API_URL ) ) return next.handle( req );

		// For specific URLs (e.g. register-contact): do not add auth, and remove any existing Authorization header
		if ( AuthInterceptor.SKIP_AUTH_HEADER_URLS.some( url => req.url.includes( url ) ) ) {
			const reqWithoutAuth = req.clone( { headers: req.headers.delete( 'Authorization' ) } );
			return next.handle( reqWithoutAuth );
		}

		return from( this.addAuthHeader( req ) ).pipe(
			mergeMap( ( authReq ) => next.handle( authReq ) ),
			// Re-throw server HTTP errors so downstream interceptors handle them.
			// Suppress the observable when signout was already triggered (token refresh failed),
			// so the unauthenticated request is never sent and error.interceptor doesn't fire a second signout.
			catchError( ( err: unknown ) => {
				if ( err instanceof HttpErrorResponse ) return throwError( () => err );
				if ( err instanceof AuthRefreshFailedError ) return EMPTY;
				return next.handle( req );
			} )
		);
	}

	private async addAuthHeader ( req: HttpRequest<any> ): Promise<HttpRequest<any>> {
		// Prefer Keycloak path when context exists
		const keycloakContext = getKeycloakContext();
		if ( keycloakContext?.authProvider === 'keycloak' ) {
			try {
				const token = await this.dynamicKeycloakService.ensureFreshToken();
				return req.clone( { setHeaders: { authorization: token } } );
			} catch {
				// Refresh token is expired or Keycloak is unreachable — sign out and suppress the request.
				await this.authService.signOutUser();
				throw new AuthRefreshFailedError();
			}
		}

		// 2. Fallback to Legacy
		const userData = JSON.parse( localStorage.getItem( jwtTokenConfig.storagePathUI ) );
		if ( userData?.token ) {
			return req.clone( { setHeaders: { authorization: userData.token } } );
		}

		return req;
	}

}
